import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { $, fH, fN, barColor } from './utils';
import { Logo, UserChip, MenuLink } from './PageHeader';

/*
 * Team Hours: logged hours vs capacity per client, department, day and person.
 * Source: people, daily_hours and daily_pto (COR -> n8n -> Supabase). Those tables are RLS-gated on
 * 'teamhours' or 'finance' (migration 20261029_team_hours_finance.sql). An empty result is reported
 * as missing access, see TeamHoursPage.
 */

const INTERNAL_CLIENTS = new Set(['INPULSE [Internal]', 'Internal/No Client']);
// CEO has no department of its own: folds into Operations
const DEPT_REMAP = { CEO: 'Operations' };
const normDept = d => DEPT_REMAP[d] || d || 'Unassigned';
/* Client-facing departments whose capacity is expected to convert into billable work. Everything else
   (New Business, HR, Operations incl. CEO, Finance, Unassigned) is overhead: it appears in the "All hours"
   row but adds no billable capacity and no missed-hours cost. Post-normDept names. Individual exceptions
   live in people.billable_override (null = derive from department, false/true = force). */
const BILLABLE_DEPTS = new Set(['Creative Team', 'Client Services', 'Media Team', 'Strategy & BI', 'PR Influencer Marketing']);
const isBillablePerson = r => (r && r.billable_override !== null && r.billable_override !== undefined)
  ? !!r.billable_override
  : BILLABLE_DEPTS.has(r ? r.department : '');

const TABS = [
  { id: 'client', label: 'Hours by Client' },
  { id: 'dept',   label: 'Hours by Department' },
  { id: 'daily',  label: 'Daily · Logged vs Capacity' },
  { id: 'people', label: 'People · Billable %' },
];

const isoDate = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* Default range: 1st of the current month -> yesterday. Today is excluded on purpose: it adds a full day of
   capacity while only part of its hours are logged. On the 1st, "yesterday" is last month, so show that month. */
function defaultRange() {
  const now = new Date(), y = new Date();
  y.setDate(y.getDate() - 1);
  const sameMonth = y.getMonth() === now.getMonth() && y.getFullYear() === now.getFullYear();
  const a = sameMonth ? now : y;
  return { from: `${a.getFullYear()}-${String(a.getMonth() + 1).padStart(2, '0')}-01`, to: isoDate(y) };
}

// PostgREST caps rows per request (1000), so page through. The first request also returns the exact row count,
// so the remaining pages are fetched concurrently (bounded) instead of one after another.
const PAGE = 1000, MAX_PARALLEL = 6;
async function fetchAll(table, cols, orderCol, filterFn) {
  const page = async (start, withCount) => {
    let q = supabase.from(table).select(cols, withCount ? { count: 'exact' } : undefined).order(orderCol);
    if (filterFn) q = filterFn(q);
    const { data, count, error } = await q.range(start, start + PAGE - 1);
    if (error) throw new Error(`${table}: ${error.message}`);
    return { data, count };
  };
  const first = await page(0, true);
  const total = first.count ?? first.data.length;
  const starts = [];
  for (let s = PAGE; s < total; s += PAGE) starts.push(s);
  if (!starts.length) return first.data;
  const rest = new Array(starts.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL, starts.length) }, async () => {
    while (next < starts.length) { const i = next++; rest[i] = (await page(starts[i])).data; }
  }));
  return first.data.concat(...rest);
}

// Only the columns the page uses (daily_hours has 16).
const COLS = {
  people: 'user_id,person,department,daily_hours,is_freelancer,start_date,end_date,billable_override',
  hours: 'entry_id,date,user_id,person,department,is_freelancer,client,project,project_id,hours,cost',
  pto: 'pto_id,date,user_id,person,department,is_pto,hours',
};

// Loaded ranges are kept for a few minutes so switching back to one is instant; the promise is cached so
// concurrent requests for the same range share one fetch. The Refresh button bypasses it.
const CACHE_MS = 5 * 60 * 1000, CACHE_MAX = 6;
const cache = new Map();
function loadData(from, to, force) {
  const key = `${from}|${to}`, hit = cache.get(key);
  if (!force && hit && Date.now() - hit.t < CACHE_MS) return hit.p;
  const p = fetchData(from, to);
  cache.delete(key);
  cache.set(key, { p, t: Date.now() });
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
  p.catch(() => cache.delete(key));
  return p;
}

async function fetchData(from, to) {
  // daily_hours and daily_pto are soft-delete tables (n8n stamps deleted_at): always exclude deleted rows,
  // including in "All dates" mode.
  const rangeFilter = q => {
    if (from) q = q.gte('date', from);
    if (to) q = q.lte('date', to);
    return q.is('deleted_at', null);
  };
  const [roster, hours, pto] = await Promise.all([
    fetchAll('people', COLS.people, 'user_id', q => q.eq('is_active', true)), // is_active=false = departed
    fetchAll('daily_hours', COLS.hours, 'entry_id', rangeFilter),
    fetchAll('daily_pto', COLS.pto, 'pto_id', rangeFilter),
  ]);
  return {
    roster: roster.map(r => ({
      user_id: r.user_id, person: r.person, department: normDept(r.department),
      daily_hours: Number(r.daily_hours) || 0,
      is_freelancer: !!r.is_freelancer,
      start_date: (r.start_date || '').slice(0, 10) || null,
      end_date: (r.end_date || '').slice(0, 10) || null,
      billable_override: (r.billable_override === null || r.billable_override === undefined) ? null : !!r.billable_override,
    })),
    hours: hours.map(h => ({
      user_id: h.user_id, date: (h.date || '').slice(0, 10), person: h.person,
      department: normDept(h.department), is_freelancer: !!h.is_freelancer,
      client: h.client || 'Internal/No Client',
      project: h.project || '', project_id: h.project_id == null ? null : String(h.project_id),
      hours: Number(h.hours) || 0, cost: Number(h.cost) || 0,
    })).filter(h => h.date),
    pto: pto.map(p => ({
      user_id: p.user_id, date: (p.date || '').slice(0, 10), person: p.person,
      department: normDept(p.department), is_pto: !!p.is_pto, hours: Number(p.hours) || 0,
    })).filter(p => p.date),
  };
}

/* ---------- Rate Card basis ----------
   Mirrors what Rate Card vs COR counts as client "actual" hours: client work only, minus excluded clients and
   one-time projects, minus projects it books as internal buckets. It does NOT reproduce the fixed-cost
   fill-ins (planned hours substituted for plan rows with nothing logged) or the full-calendar-month range. */
const rcNorm = s => (s || '').replace(/’/g, "'").trim();

function rcExclusionSets(rows) {
  const of = k => new Set((rows || []).filter(r => r.kind === k).map(r => r.name));
  return { clients: of('client'), projects: of('project'), projectIds: of('project_id') };
}
const rcKeep = (h, ex) =>
  !INTERNAL_CLIENTS.has(rcNorm(h.client)) && !ex.clients.has(rcNorm(h.client)) &&
  !ex.projects.has(rcNorm(h.project)) && !(h.project_id && ex.projectIds.has(h.project_id));

/* ---------- calculations ---------- */

function workingDays(from, to) {
  const d = new Date(from + 'T00:00:00'), end = new Date(to + 'T00:00:00');
  let n = 0;
  while (d <= end) { const wd = d.getDay(); if (wd !== 0 && wd !== 6) n++; d.setDate(d.getDate() + 1); }
  return n;
}
/* Working days for ONE person: the range clipped to their employment window, so a leaver keeps the capacity
   of months they were here and a joiner starts counting at start_date. */
function workingDaysFor(r, from, to) {
  const s = (r && r.start_date && r.start_date > from) ? r.start_date : from;
  const e = (r && r.end_date && r.end_date < to) ? r.end_date : to;
  if (!s || !e || s > e) return 0;
  return workingDays(s, e);
}
const inWindow = (r, d) => (!r.start_date || d >= r.start_date) && (!r.end_date || d <= r.end_date);

const median = arr => {
  if (!arr.length) return 0;
  const s = [...arr].sort((x, y) => x - y), i = Math.floor(s.length / 2);
  return s.length % 2 ? s[i] : (s[i - 1] + s[i]) / 2;
};

/* Single source of truth for capacity, so the KPIs, daily chart and people table cannot drift apart.
     daily_hours > 0 -> (daily_hours x working days) - PTO - holiday, floored at 0
     daily_hours = 0 -> capacity := hours logged (no contracted target: counted as exactly 100% utilised;
                        PTO is deliberately not netted or utilisation would exceed 100%)
   Hourly rate is an effective COST rate (daily_hours.cost / hours), not a bill rate; falls back to the
   department median, then the global median. */
function buildMetrics(hoursArr, ptoArr, { roster, from, to, dep }) {
  const wd = workingDays(from, to);
  const rosterMap = {};
  roster.forEach(r => { rosterMap[r.user_id] = r; });
  const m = {};
  const get = (uid, person, department, fl) => m[uid] || (m[uid] = {
    user_id: uid, person, department, fl: !!fl,
    logged: 0, billable: 0, internal: 0, pto: 0, holiday: 0,
    costSum: 0, costedHours: 0, clients: new Set(),
  });
  hoursArr.forEach(h => {
    const a = get(h.user_id, h.person, h.department, h.is_freelancer);
    a.logged += h.hours;
    if (INTERNAL_CLIENTS.has(h.client)) a.internal += h.hours;
    else { a.billable += h.hours; a.clients.add(h.client); }
    if (h.cost > 0 && h.hours > 0) { a.costSum += h.cost; a.costedHours += h.hours; }
  });
  ptoArr.forEach(p => {
    const a = get(p.user_id, p.person, p.department, false);
    if (p.is_pto) a.pto += p.hours; else a.holiday += p.hours;
  });
  // Seed people with a target who were on the roster in range but logged nothing and took no time off:
  // a real 0% utilisation belongs in the numbers rather than silently vanishing.
  roster.forEach(r => {
    if (m[r.user_id]) return;
    if (!(r.daily_hours > 0)) return;
    if (dep && r.department !== dep) return;
    if (workingDaysFor(r, from, to) <= 0) return;
    get(r.user_id, r.person, r.department, r.is_freelancer);
  });
  const derived = Object.values(m).filter(a => a.costedHours > 0);
  const globalMed = median(derived.map(a => a.costSum / a.costedHours));
  const deptBuckets = {};
  derived.forEach(a => { (deptBuckets[a.department] || (deptBuckets[a.department] = [])).push(a.costSum / a.costedHours); });
  const deptMed = {};
  Object.entries(deptBuckets).forEach(([d, arr]) => { deptMed[d] = median(arr); });

  const rows = Object.values(m).map(a => {
    const r = rosterMap[a.user_id];
    const hasTarget = !!r && r.daily_hours > 0;
    const wdP = r ? workingDaysFor(r, from, to) : wd;
    const capacity = hasTarget ? Math.max(0, r.daily_hours * wdP - a.pto - a.holiday) : a.logged;
    const rateDerived = a.costedHours > 0;
    const rate = rateDerived ? a.costSum / a.costedHours : (deptMed[a.department] || globalMed);
    const isBill = isBillablePerson(r || { department: a.department, billable_override: null });
    const billCap = isBill ? capacity : 0;
    const missed = Math.max(0, billCap - a.billable); // 100%-of-capacity baseline, floored per person
    return {
      ...a, hasTarget, capacity, rate, rateDerived, isBill, billCap, missed,
      missedCost: missed * rate,
      util: capacity > 0 ? a.logged / capacity * 100 : 0,
      billable_pct: a.logged > 0 ? a.billable / a.logged * 100 : 0,
      billable_util: capacity > 0 ? a.billable / capacity * 100 : 0,
      nclients: a.clients.size,
    };
  });
  return { rows, wd };
}

/* ---------- small UI pieces ---------- */

const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', marginBottom: 22 };
const fieldLabel = { display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 5 };
const input = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' };
const mono = { fontFamily: 'DM Mono,monospace', fontSize: 12 };

function Field({ label, children }) {
  return <div><label style={fieldLabel}>{label}</label>{children}</div>;
}

function Kpi({ label, value, detail, color }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', boxShadow: 'var(--shadow)', borderLeft: `3px solid ${color || 'var(--red)'}` }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text3)' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, letterSpacing: '-0.5px' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{detail}</div>
    </div>
  );
}

function KpiRow({ title, note, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 9 }}>
        <span><b style={{ color: 'var(--text)' }}>{title}</b> — {note}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>{children}</div>
    </div>
  );
}

function CardHeader({ title, caption }) {
  return (
    <>
      <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 3 }}>{title}</h3>
      <div style={{ ...mono, color: 'var(--text2)', marginBottom: 18 }}>{caption}</div>
    </>
  );
}

const Empty = () => <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '60px 0', ...mono }}>No data in range</div>;

function HBarChart({ data, color }) {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...sorted.map(d => d.value));
  const rowH = 30, padL = 190, padR = 70, top = 8, w = 900;
  const h = top * 2 + sorted.length * rowH, barW = w - padL - padR;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMinYMin meet" style={{ display: 'block', width: '100%' }}>
      {sorted.map((d, i) => {
        const y = top + i * rowH, bw = Math.max(2, d.value / max * barW);
        const lbl = d.label.length > 26 ? d.label.slice(0, 25) + '…' : d.label;
        return (
          <g key={d.label}>
            <title>{`${d.label} — ${fH(d.value)}h`}</title>
            <text x={padL - 12} y={y + rowH / 2} textAnchor="end" dominantBaseline="middle" fontSize="12.5" fill="var(--text)" fontWeight="500">{lbl}</text>
            <rect x={padL} y={y + 5} width={barW} height={rowH - 12} rx="4" fill="var(--card2)" />
            <rect x={padL} y={y + 5} width={bw} height={rowH - 12} rx="4" fill={color} />
            <text x={padL + bw + 8} y={y + rowH / 2} dominantBaseline="middle" fontSize="11.5" fill="var(--text2)" fontFamily="DM Mono,monospace">{fH(d.value)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function LegendItem({ color, dashed, children }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <i style={dashed
        ? { width: 18, height: 0, borderTop: `2px dashed ${color}`, display: 'inline-block' }
        : { width: 14, height: 14, borderRadius: 3, background: color, display: 'inline-block' }} />
      {children}
    </span>
  );
}

/* Daily capacity mirrors buildMetrics so chart and KPIs agree:
   people WITH a target contribute a fixed daily_hours ceiling on working days (0 at weekends), less their own
   PTO/holiday that day; people WITHOUT a target contribute exactly what they logged that day (PTO not netted).
   Membership on a given day follows the employment window (start_date / end_date). */
function DailyChart({ hours, pto, roster, dep }) {
  const byDate = {}, byPto = {}, byHol = {};
  hours.forEach(h => { byDate[h.date] = (byDate[h.date] || 0) + h.hours; });
  pto.forEach(p => { const b = p.is_pto ? byPto : byHol; b[p.date] = (b[p.date] || 0) + p.hours; });
  const dates = [...new Set([...Object.keys(byDate), ...Object.keys(byPto), ...Object.keys(byHol)])].sort();
  if (!dates.length) return <Empty />;

  const noTarget = new Set(roster.filter(r => !(r.daily_hours > 0)).map(r => r.user_id));
  const contributors = roster.filter(r => (!dep || r.department === dep) && r.daily_hours > 0);
  const offTargeted = {}, capZero = {};
  pto.forEach(p => { if (!noTarget.has(p.user_id)) offTargeted[p.date] = (offTargeted[p.date] || 0) + p.hours; });
  hours.forEach(h => { if (noTarget.has(h.user_id)) capZero[h.date] = (capZero[h.date] || 0) + h.hours; });
  const cap = {}, netCap = {};
  dates.forEach(d => {
    const wd = new Date(d + 'T00:00:00').getDay();
    const base = (wd === 0 || wd === 6) ? 0 : contributors.reduce((s, r) => s + (inWindow(r, d) ? r.daily_hours : 0), 0);
    cap[d] = base + (capZero[d] || 0);
    netCap[d] = Math.max(0, base - (offTargeted[d] || 0)) + (capZero[d] || 0);
  });

  const maxV = Math.max(1, ...dates.map(d => Math.max((byDate[d] || 0) + (byPto[d] || 0) + (byHol[d] || 0), cap[d])));
  const W = Math.max(900, dates.length * 38), H = 360, padL = 54, padB = 58, padT = 14;
  const plotW = W - padL - 20, plotH = H - padT - padB;
  const step = plotW / dates.length, bw = Math.min(26, step * 0.6);
  const yOf = v => padT + plotH - (v / maxV * plotH);
  const base = padT + plotH;
  const target = dates.map((d, i) => `${i === 0 ? 'M' : 'L'}${padL + step * i + step / 2} ${yOf(netCap[d] * 0.75)}`).join(' ');

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMin meet" style={{ display: 'block', width: '100%', minWidth: Math.min(W, 900) }}>
        {[0, 1, 2, 3, 4].map(g => {
          const v = maxV * g / 4, y = yOf(v);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={W - 20} y2={y} stroke="var(--border)" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="var(--text3)" fontFamily="DM Mono,monospace">{fN(v, 0)}</text>
            </g>
          );
        })}
        {dates.map((d, i) => {
          const cx = padL + step * i + step / 2;
          const logged = byDate[d] || 0, p = byPto[d] || 0, hol = byHol[d] || 0;
          const capY = yOf(cap[d]), logY = yOf(logged), ptoY = yOf(logged + p), holY = yOf(logged + p + hol);
          const pctTxt = netCap[d] > 0 ? ` (${fN(logged / netCap[d] * 100, 0)}%)` : '';
          const show = dates.length <= 40 || i % Math.ceil(dates.length / 30) === 0;
          return (
            <g key={d}>
              <title>{`${d} · logged ${fH(logged)}h${p ? ` · PTO ${fH(p)}h` : ''}${hol ? ` · holiday ${fH(hol)}h` : ''} / capacity ${fH(netCap[d])}h net${pctTxt}`}</title>
              <rect x={cx - bw / 2} y={capY} width={bw} height={base - capY} rx="3" fill="var(--bar-idle)" />
              <rect x={cx - bw / 2 + 2} y={logY} width={bw - 4} height={base - logY} rx="3" fill="var(--green)" />
              {p > 0 && <rect x={cx - bw / 2 + 2} y={ptoY} width={bw - 4} height={logY - ptoY} fill="var(--amber)" />}
              {hol > 0 && <rect x={cx - bw / 2 + 2} y={holY} width={bw - 4} height={ptoY - holY} fill="var(--blue)" />}
              {show && <text x={cx} y={H - padB + 16} textAnchor="middle" fontSize="10" fill="var(--text3)" fontFamily="DM Mono,monospace">{d.slice(8, 10)}</text>}
              {show && (d.slice(8, 10) === '01' || i === 0) && <text x={cx} y={H - padB + 30} textAnchor="middle" fontSize="9" fill="var(--red)" fontFamily="DM Mono,monospace">{d.slice(0, 7)}</text>}
            </g>
          );
        })}
        <path d={target} fill="none" stroke="var(--red)" strokeWidth="2" strokeDasharray="5 4" opacity="0.85" />
      </svg>
    </div>
  );
}

const PEOPLE_COLS = [
  ['person', 'Name'], ['department', 'Dept'], ['logged', 'Logged h'], ['pto', 'PTO h'], ['holiday', 'Holiday h'],
  ['capacity', 'Capacity h (net)'], ['billable', 'Billable h'], ['internal', 'Internal h'],
  ['billable_pct', 'Billable %'], ['billable_util', 'Billable Util %'], ['missed', 'Missed h'],
  ['missedCost', 'Missed $'], ['nclients', '# Clients'], ['util', 'Util %'],
];

function PeopleTable({ rows, wd, sort, onSort }) {
  const sorted = useMemo(() => [...rows].sort((x, y) => {
    const a = x[sort.k], b = y[sort.k];
    if (typeof a === 'string') return sort.d === 'desc' ? b.localeCompare(a) : a.localeCompare(b);
    return sort.d === 'desc' ? b - a : a - b;
  }), [rows, sort]);

  const T = { logged: 0, pto: 0, holiday: 0, capacity: 0, billable: 0, internal: 0, missed: 0, missedCost: 0 };
  rows.forEach(r => {
    T.logged += r.logged; T.pto += r.pto; T.holiday += r.holiday; T.capacity += r.capacity;
    T.billable += r.billable; T.internal += r.internal;
    if (r.isBill) { T.missed += r.missed; T.missedCost += r.missedCost; }
  });
  const noTgt = rows.filter(r => !r.hasTarget).length;
  const th = { ...fieldLabel, display: 'table-cell', marginBottom: 0, textAlign: 'left', padding: '10px 12px', borderBottom: '1.5px solid var(--border)', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' };
  const td = { padding: '9px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', fontSize: 13 };
  const tot = { ...td, ...mono, fontWeight: 700, borderTop: '2px solid var(--green)', background: 'var(--card2)' };

  return (
    <div style={card}>
      <CardHeader
        title="People — Utilization"
        caption={`${rows.length} people with logged or PTO hours · capacity = (daily_hours × ${wd} working days) net of PTO/holiday${noTgt ? ` · ${noTgt} without a target counted at hours logged` : ''} · Missed = billable capacity − billable hours, blank for non-billable depts`}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {PEOPLE_COLS.map(([k, label]) => (
                <th key={k} style={th} onClick={() => onSort(k)}>{label}{sort.k === k ? (sort.d === 'desc' ? ' ↓' : ' ↑') : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const low = r.util < 65; // under-utilised rows are highlighted
              const c = low ? { color: 'var(--red)', fontStyle: 'italic' } : null;
              return (
                <tr key={r.user_id}>
                  <td style={{ ...td, fontWeight: 600, ...c }}>{r.person}{r.fl && <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600, marginLeft: 6 }}>FL</span>}</td>
                  <td style={{ ...td, ...c }}>{r.department}</td>
                  <td style={{ ...td, ...mono, ...c }}>{fH(r.logged)}</td>
                  <td style={{ ...td, ...mono, ...c }}>{r.pto ? fH(r.pto) : '–'}</td>
                  <td style={{ ...td, ...mono, ...c }}>{r.holiday ? fH(r.holiday) : '–'}</td>
                  <td style={{ ...td, ...mono, ...c }}>{fN(r.capacity, 0)}</td>
                  <td style={{ ...td, ...mono, ...c }}>{fH(r.billable)}</td>
                  <td style={{ ...td, ...mono, ...c }}>{fH(r.internal)}</td>
                  <td style={{ ...td, ...mono, ...c }}>{fN(r.billable_pct, 0)}%</td>
                  <td style={{ ...td, ...mono, ...c }}>{fN(r.billable_util, 0)}%</td>
                  <td style={{ ...td, ...mono, ...c }}>{r.isBill ? fN(r.missed, 0) : '–'}</td>
                  <td style={{ ...td, ...mono, ...c }}>{r.isBill ? $(r.missedCost) : '–'}</td>
                  <td style={{ ...td, ...mono, ...c }}>{r.nclients}</td>
                  <td style={{ ...td, ...mono, ...c }}>
                    <span style={{ display: 'inline-block', width: 90, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', verticalAlign: 'middle', marginRight: 6 }}>
                      <i style={{ display: 'block', height: '100%', width: `${Math.min(100, r.util)}%`, background: barColor(r.util) }} />
                    </span>
                    {fN(r.util, 0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={tot}>TOTAL ({rows.length})</td><td style={tot} />
              <td style={tot}>{fN(T.logged, 0)}</td><td style={tot}>{fN(T.pto, 0)}</td><td style={tot}>{fN(T.holiday, 0)}</td>
              <td style={tot}>{fN(T.capacity, 0)}</td><td style={tot}>{fN(T.billable, 0)}</td><td style={tot}>{fN(T.internal, 0)}</td>
              <td style={tot}>{fN(T.logged > 0 ? T.billable / T.logged * 100 : 0, 0)}%</td>
              <td style={tot}>{fN(T.capacity > 0 ? T.billable / T.capacity * 100 : 0, 0)}%</td>
              <td style={tot}>{fN(T.missed, 0)}</td><td style={tot}>{$(T.missedCost)}</td><td style={tot} />
              <td style={tot}>{fN(T.capacity > 0 ? T.logged / T.capacity * 100 : 0, 0)}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ---------- page ---------- */

export default function TeamHoursPage({ session, onSignOut }) {
  const [range, setRange] = useState(defaultRange);       // {from,to}; nulls = "All dates"
  const [data, setData] = useState(null);                 // {roster,hours,pto} for the loaded range
  const [error, setError] = useState(null);
  const [dep, setDep] = useState('');
  const [type, setType] = useState('');
  const [ignoreHol, setIgnoreHol] = useState(false);
  const [rcBasis, setRcBasis] = useState(false);
  const [rcEx, setRcEx] = useState(null);                 // exclusion sets; null = loading, false = unavailable
  const [tab, setTab] = useState('client');
  const [sort, setSort] = useState({ k: 'logged', d: 'desc' });

  useEffect(() => {
    supabase.rpc('team_hours_rate_card_exclusions').then(({ data: rows, error: e }) => {
      setRcEx(e ? false : rcExclusionSets(rows)); // unavailable until the migration is applied
    });
  }, []);

  const seenReload = useRef(0);
  const [busy, setBusy] = useState(true);
  const [reload, setReload] = useState(0);               // bumped by Refresh to bypass the cache

  useEffect(() => {
    let live = true;
    setBusy(true);
    loadData(range.from, range.to, reload > 0 && reload !== seenReload.current)
      .then(d => { if (live) { setData(d); setError(null); } })
      .catch(e => { if (live) setError(e.message); })
      .finally(() => { if (live) setBusy(false); });
    seenReload.current = reload;
    return () => { live = false; };
  }, [range, reload]);

  // Effective dates: "All dates" shows the span of the loaded data in the inputs
  const span = useMemo(() => {
    const ds = (data?.hours || []).map(h => h.date).sort();
    return { from: ds[0], to: ds[ds.length - 1] };
  }, [data]);
  const from = range.from || span.from || isoDate(new Date());
  const to = range.to || span.to || isoDate(new Date());

  const view = useMemo(() => {
    if (!data) return null;
    const byDept = h => !dep || h.department === dep;
    // Date range is already applied by the query; re-check so a stale list never leaks across a range change.
    const inRange = x => x.date >= from && x.date <= to;
    const useRc = rcBasis && rcEx;
    const hoursNoType = data.hours.filter(h => inRange(h) && byDept(h) && (!useRc || rcKeep(h, rcEx)));
    const hours = hoursNoType.filter(h => {
      const internal = INTERNAL_CLIENTS.has(h.client);
      return type === 'billable' ? !internal : type === 'internal' ? internal : true;
    });
    const pto = data.pto.filter(p => inRange(p) && byDept(p) && (!ignoreHol || p.is_pto));
    const ctx = { roster: data.roster, from, to, dep };
    return { hours, hoursNoType, pto, ctx, depts: [...new Set(data.hours.map(h => h.department))].sort() };
  }, [data, from, to, dep, type, ignoreHol, rcBasis, rcEx]);

  const calc = useMemo(() => {
    if (!view) return null;
    const all = buildMetrics(view.hours, view.pto, view.ctx);
    // Billable row ignores the Type filter: "Internal only" would otherwise zero billable hours and report
    // the whole capacity as missed.
    const noType = buildMetrics(view.hoursNoType, view.pto, view.ctx).rows;
    return { all, noType };
  }, [view]);

  const setDates = (f, t) => { if (f && t) setRange({ from: f, to: t }); };
  const onSort = k => setSort(s => ({ k, d: s.k === k && s.d === 'desc' ? 'asc' : 'desc' }));

  let body;
  if (error) {
    body = <div style={{ padding: 60, textAlign: 'center', color: 'var(--red)' }}>Could not load: {error}</div>;
  } else if (!view) {
    body = <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>;
  } else if (!data.roster.length && !data.hours.length) {
    body = (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>
        No data was returned. The team hours tables are readable with the <b>finance</b> or <b>teamhours</b> access flag.
        If you have one of them and this persists, check that the latest Supabase migration has been applied.
      </div>
    );
  } else {
    body = (
      <>
        <Loaded view={view} calc={calc} tab={tab} setTab={setTab} sort={sort} onSort={onSort} dep={dep} from={from} to={to} roster={data.roster} />
        <RateCardNote active={rcBasis && !!rcEx} />
      </>
    );
  }

  return (
    <div style={{ maxWidth: 1480, margin: '0 auto', padding: '24px 24px 60px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <MenuLink />
          <Logo />
        </div>
        <UserChip session={session} onSignOut={onSignOut} />
      </header>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Team Hours</h2>
      <div style={{ ...mono, color: 'var(--text2)', marginBottom: 16 }}>
        {busy ? 'Loading from Supabase…' : data ? `${data.hours.length.toLocaleString()} entries · ${data.roster.length} people · ${from} → ${to}` : ''}
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 22 }}>
        <Field label="From"><input type="date" style={input} value={from} onChange={e => setDates(e.target.value, to)} /></Field>
        <Field label="To"><input type="date" style={input} value={to} onChange={e => setDates(from, e.target.value)} /></Field>
        <Field label="Department">
          <select style={input} value={dep} onChange={e => setDep(e.target.value)}>
            <option value="">All departments</option>
            {(view?.depts || []).map(d => <option key={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select style={input} value={type} onChange={e => setType(e.target.value)}>
            <option value="">All</option>
            <option value="billable">Billable only</option>
            <option value="internal">Internal only</option>
          </select>
        </Field>
        <Field label="Holidays">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, padding: '9px 0' }}>
            <input type="checkbox" checked={ignoreHol} onChange={e => setIgnoreHol(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--green)' }} /> Ignore
          </label>
        </Field>
        <Field label="Basis">
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '9px 0', cursor: rcEx ? 'pointer' : 'not-allowed', opacity: rcEx ? 1 : 0.5 }}
            title={rcEx === false ? 'Not available yet: the team_hours_rate_card_exclusions migration has not been applied' : 'Count hours the way Rate Card vs COR does (see the note at the bottom)'}
          >
            <input type="checkbox" disabled={!rcEx} checked={rcBasis && !!rcEx} onChange={e => setRcBasis(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--green)' }} /> Rate Card
          </label>
        </Field>
        <button
          onClick={() => setRange({ from: null, to: null })}
          style={{ ...input, cursor: 'pointer', fontWeight: 600, color: 'var(--text2)' }}
        >All dates</button>
        <button
          onClick={() => setReload(n => n + 1)}
          disabled={busy}
          title="Reload from Supabase (ranges are cached for 5 minutes)"
          style={{ ...input, cursor: busy ? 'wait' : 'pointer', fontWeight: 600, color: 'var(--text2)' }}
        >Refresh</button>
        <div style={{ flex: 1 }} />
        {view && <div style={{ ...mono, color: 'var(--text2)', alignSelf: 'center' }}>{view.hours.length.toLocaleString()} entries shown</div>}
      </div>

      <div style={{ opacity: busy ? 0.45 : 1, pointerEvents: busy ? 'none' : 'auto', transition: 'opacity .15s' }}>{body}</div>
    </div>
  );
}

function RateCardNote({ active }) {
  const li = { marginBottom: 6 };
  return (
    <div style={{ marginTop: 20, padding: '14px 18px', background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text2)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
        Why these numbers differ from Rate Card vs COR{active && <span style={{ color: 'var(--green)', fontWeight: 600 }}> · Rate Card basis is ON</span>}
      </div>
      <ul style={{ paddingLeft: 18 }}>
        <li style={li}><b>Scope.</b> By default this page counts every hour logged in COR, internal entries included. Rate Card vs COR counts client work only.</li>
        <li style={li}><b>Exclusions.</b> Rate Card drops the clients and one-time projects on its exclusion list, and hours on projects it books as internal buckets.</li>
        <li style={li}><b>Fixed-cost fill-ins.</b> For a fixed-cost plan row (client + person) with no hours logged in the month, Rate Card counts the planned hours as actual. This page never adds hours that were not logged, so a client with fixed-cost rows can read higher on Rate Card.</li>
        <li style={li}><b>Period.</b> Rate Card always covers a full calendar month. This page uses the From/To range you pick.</li>
      </ul>
      <div>
        The <b>Rate Card</b> toggle applies the first two points (client work only, minus the exclusions). It does not add the fixed-cost fill-ins or change the period,
        so a client with fill-ins will still show fewer hours here than on Rate Card.
      </div>
    </div>
  );
}

function Loaded({ view, calc, tab, setTab, sort, onSort, dep, from, to, roster }) {
  const { hours, pto } = view;
  const { all, noType } = calc;
  const wd = all.wd;

  // Row 1: all hours (respects every filter, including Type)
  const logged = all.rows.reduce((s, a) => s + a.logged, 0);
  const billable = all.rows.reduce((s, a) => s + a.billable, 0);
  const capacity = all.rows.reduce((s, a) => s + a.capacity, 0);
  const ptoAll = all.rows.reduce((s, a) => s + a.pto + a.holiday, 0);
  const clients = new Set(hours.filter(h => !INTERNAL_CLIENTS.has(h.client)).map(h => h.client)).size;
  const noTarget = all.rows.filter(a => !a.hasTarget).length;

  // Row 2: billable departments only
  const bill = noType.filter(a => a.isBill);
  const excluded = noType.filter(a => !a.isBill && BILLABLE_DEPTS.has(a.department)).length;
  const bLogged = bill.reduce((s, a) => s + a.billable, 0);
  const bCap = bill.reduce((s, a) => s + a.billCap, 0);
  const bMissed = bill.reduce((s, a) => s + a.missed, 0);
  const bMissedCost = bill.reduce((s, a) => s + a.missedCost, 0);
  const estimated = bill.filter(a => !a.rateDerived && a.missed > 0).length;
  const billDepts = new Set(bill.map(a => a.department)).size;

  const sumBy = key => {
    const m = {};
    hours.forEach(h => { m[h[key]] = (m[h[key]] || 0) + h.hours; });
    return Object.entries(m).map(([label, value]) => ({ label, value }));
  };
  const clientData = sumBy('client'), deptData = sumBy('department');

  return (
    <>
      <KpiRow title="All hours" note="everyone who logged or took time off">
        <Kpi color="var(--green)" label="Logged Hours" value={fN(logged, 0)} detail={`${hours.length.toLocaleString()} entries · ${all.rows.length} of ${roster.length} people`} />
        <Kpi color="var(--amber)" label="Capacity" value={fN(capacity, 0)} detail={`${wd} working days, net of ${fN(ptoAll, 0)}h PTO/holiday${noTarget ? ` · ${noTarget} w/o target counted at logged` : ''}`} />
        <Kpi label="Utilization" value={`${fN(capacity > 0 ? logged / capacity * 100 : 0, 0)}%`} detail="logged ÷ capacity" />
        <Kpi color="var(--blue)" label="Billable Mix" value={`${fN(logged > 0 ? billable / logged * 100 : 0, 0)}%`} detail={`of logged hours · ${fN(billable, 0)}h billable · ${fN(logged - billable, 0)}h internal`} />
        <Kpi label="Active Clients" value={clients} detail="excl. internal" />
      </KpiRow>

      <KpiRow
        title="Billable only"
        note={bill.length
          ? `${bill.length} people in ${billDepts} client-facing departments${excluded ? ` · ${excluded} excluded as internal by role` : ''}`
          : 'no billable departments in current filter'}
      >
        {bill.length ? (
          <>
            <Kpi color="var(--green)" label="Billable Logged" value={fN(bLogged, 0)} detail="client work only, excl. internal" />
            <Kpi color="var(--amber)" label="Billable Capacity" value={fN(bCap, 0)} detail="net of PTO/holiday" />
            <Kpi label="Billable Utilization" value={`${fN(bCap > 0 ? bLogged / bCap * 100 : 0, 0)}%`} detail="billable ÷ billable capacity" />
            <Kpi color="var(--blue)" label="Missed Billable Hrs" value={fN(bMissed, 0)} detail="vs 100% of capacity" />
            <Kpi label="Cost of Missed Hrs" value={$(bMissedCost)} detail={`idle-capacity cost at internal rates, not lost revenue${estimated ? ` · ${estimated} est. rate` : ''}`} />
          </>
        ) : (
          <Kpi label="Billable" value="–" detail="current department filter has no client-facing departments" />
        )}
      </KpiRow>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid var(--border)', marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '11px 20px', fontSize: 14, fontWeight: 600,
              color: tab === t.id ? 'var(--green)' : 'var(--text2)',
              borderBottom: `2.5px solid ${tab === t.id ? 'var(--green)' : 'transparent'}`, marginBottom: -1.5,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'client' && (
        <div style={card}>
          <CardHeader title="Hours by Client" caption={`Total logged hours per client · ${clientData.length} clients`} />
          {clientData.length ? <HBarChart data={clientData} color="var(--green)" /> : <Empty />}
        </div>
      )}
      {tab === 'dept' && (
        <div style={card}>
          <CardHeader title="Hours by Department" caption={`Total logged hours per department · ${deptData.length} departments`} />
          {deptData.length ? <HBarChart data={deptData} color="var(--blue)" /> : <Empty />}
        </div>
      )}
      {tab === 'daily' && (
        <div style={card}>
          <CardHeader title="Daily Hours — Logged vs Capacity" caption="Logged + PTO + holiday vs capacity, with 75% minimum target (net of time off)" />
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, fontSize: 12.5, color: 'var(--text2)' }}>
            <LegendItem color="var(--green)">Logged in COR</LegendItem>
            <LegendItem color="var(--amber)">PTO (personal)</LegendItem>
            <LegendItem color="var(--blue)">Company holiday</LegendItem>
            <LegendItem color="var(--bar-idle)">Capacity (Σ daily hours)</LegendItem>
            <LegendItem color="var(--red)" dashed>75% minimum target (net of time off)</LegendItem>
          </div>
          <DailyChart hours={hours} pto={pto} roster={roster} dep={dep} />
        </div>
      )}
      {tab === 'people' && <PeopleTable rows={all.rows} wd={wd} sort={sort} onSort={onSort} />}

      <div style={{ marginTop: 28, ...mono, fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
        data via COR → n8n → Supabase · capacity = (daily_hours × working days), net of PTO/holiday hours · internal = INPULSE / no-client entries · {from} → {to}
      </div>
    </>
  );
}
